import React, { useEffect, useState } from "react";
import { Card, CircularProgress, TextField } from "@mui/material";
import Button from "../../Subcomponents/Button";
import cssClasses from "./BankVerification.module.css";
import { useNavigate } from "react-router-dom";
import { useLocation } from "react-router-dom";
import { useDispatch } from "react-redux";
import {
  getApplicantsForEmandate,
  getBankAccountVerificationStatusForEmandate,
} from "../../Services/Allotment";
import EMandateStepper from "./EMandateStepper";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import { numberWithCommas } from "../../Assets/Common/CommonFunctions";
import actions from "../../Redux Store/actions";
import { toast } from "react-toastify";
import { dismissLoader, presentLoader } from "../../Services/loader";
import CustomerBankInfoMismatchModal from "../../Modals/EMandate/CustomerBankInfoMismatchModal";

// Helper function to validate IFSC Code format
// const isValidIFSC = (ifsc) => {
//   const regex = /^[A-Za-z]{4}\d{7}$/;
//   return regex.test(ifsc);
// };

// Updated IFSC validation to match the format: 4 letters, followed by '0', followed by 6 alphanumeric characters
const isValidIFSC = (ifsc) => {
  const regex = /^[A-Za-z]{4}0[A-Za-z0-9]{6}$/;
  return regex.test(ifsc);
};

const BankVerification = ({ onBack, onVerify }) => {
  const location = useLocation();
  const dispatch = useDispatch();
  const navigate = useNavigate();

  // console.log("📥 BankVerification → location.state", location.state);

  const currentIndex = location.state?.currentIndex ?? 0;
  const activeApplicant =
    location.state?.currentApplicant ||
    location.state?.currentApplicant[currentIndex];
  const totalAmount = location.state?.totalAmount || 0;
  const totalMandateAmount = location.state?.totalMandateAmount || 0;
  const bookingId = location.state?.bookingId;
  const allotment = location.state?.allotment;
  const allotmentPaymentId = location.state?.allotmentPaymentId;
  const splitAmount = location.state.splitAmount;
  const splitRatio = location.state.splitRatio;

  // console.log("👤 BankVerification → activeApplicant", activeApplicant);

  const [activeStep, setActiveStep] = useState(2);
  const [bankDetails, setBankDetails] = useState({
    accountNumber: "",
    confirmAccountNumber: "",
    ifscCode: "",
  });
  const [errors, setErrors] = useState({
    accountNumber: "",
    confirmAccountNumber: "",
    ifscCode: "",
  });
  const [isLoading, setIsLoading] = useState(false); // Track loading state

  const [isVerified, setIsVerified] = useState(false); // Track if verification is successful
  const [buttonText, setButtonText] = useState("Verify Bank Account");
  const [verificationMessage, setVerificationMessage] = useState(""); // Success/Failure message
  const [isInitialVerificationDone, setIsInitialVerificationDone] =
    useState(false);
  const [bankName, setBankName] = useState("");
  const [branchName, setBranchName] = useState("");

  const [displayNameMismatchModal, setDisplayNameMismatchModal] =
    useState(false);
  const [customerNameAsPerBankUPI, setCustomerNameAsPerBankUPI] =
    useState("N.A.");

  const handleNameMismatchModalClose = () => setDisplayNameMismatchModal(false);

  // console.log("🔄 Resetting BankVerification for applicant:", activeApplicant);

  // useEffect(() => {
  //   let totalPercentage = 0;

  //   // Sum the percentages
  //   activeApplicant.forEach((applicant) => {
  //     totalPercentage += applicant.percentage || 0;
  //   });

  //   console.log("Total Percentage:", totalPercentage);

  //   // Decide the value of isSplit based on the total percentage
  //   const isSplit =
  //     totalPercentage === activeApplicant[0].percentage ? false : true;
  //   console.log("isSplit:", isSplit);

  //   const totalAmount = totalMandateAmount; // Total Amount
  //   // const splitRatio = activeApplicant?.splitRatio; // Split ratio for active applicants
  //   const splitRatio = activeApplicant[0]?.percentage; // Split ratio for active applicants

  //   // Calculate % of the total amount
  //   const splitAmount = totalAmount * (splitRatio / 100);
  //   console.log("Split Amount:", splitAmount);
  // }, [activeApplicant, totalMandateAmount]);

  useEffect(() => {
    setBankDetails({
      accountNumber: "",
      confirmAccountNumber: "",
      ifscCode: "",
    });
    setErrors({
      accountNumber: "",
      confirmAccountNumber: "",
      ifscCode: "",
    });
    setIsVerified(false);
    setButtonText("Verify Bank Account");
    setVerificationMessage("");
    setIsInitialVerificationDone(false);
  }, [activeApplicant?.id]);

  const handleChange = (key, value) => {
    setBankDetails((prev) => ({
      ...prev,
      [key]: value,
      accountType: "Saving",
      activeApplicant: activeApplicant,
    }));

    // Reset error message if user changes IFSC, account number, or confirm account number after initial verification
    if (
      isInitialVerificationDone &&
      (key === "accountNumber" ||
        key === "ifscCode" ||
        key === "confirmAccountNumber")
    ) {
      setErrors((prev) => ({
        ...prev,
        accountNumber: "",
        ifscCode: "",
        confirmAccountNumber: "", // Reset all errors for those fields
      }));
      setIsVerified(false); // Reset verified status if any field is modified
      setVerificationMessage(""); // Reset verification message
    }
  };

  // useEffect(
  //   () => {
  //     dispatch(actions.activeMenu("emandateallotments"));
  //     const fetchApplicants = async () => {
  //       try {
  //         const agreementForm =
  //           await getApplicantsForEmandate(allotmentPaymentId);
  //         console.log("Fetched agreementForm:", agreementForm);
  //       } catch (err) {
  //         console.error("Failed to fetch applicants for eMandate:", err);
  //       }
  //     };

  //     fetchApplicants();
  //   },
  //   [allotmentPaymentId],
  //   [dispatch],
  // );

  // Validation function
  const verifyAccountDetails = () => {
    const newErrors = {
      accountNumber: "",
      confirmAccountNumber: "",
      ifscCode: "",
    };

    // Check if account number is provided
    if (!bankDetails.accountNumber) {
      newErrors.accountNumber = "Account Number is required.";
    }

    // Only check if account numbers match if both are provided
    if (bankDetails.accountNumber && bankDetails.confirmAccountNumber) {
      if (bankDetails.accountNumber !== bankDetails.confirmAccountNumber) {
        newErrors.confirmAccountNumber = "Account numbers do not match.";
      } else {
        // Clear the error message if account numbers match
        newErrors.confirmAccountNumber = "";
      }
    }

    // Check if IFSC code is valid
    if (bankDetails.ifscCode && !isValidIFSC(bankDetails.ifscCode)) {
      newErrors.ifscCode = "Invalid IFSC Code format.";
    }

    // If there are validation errors, set the errors state and return false
    setErrors(newErrors);

    // Return true only if no errors exist
    return Object.values(newErrors).every((error) => error === "");
  };

  const handleSubmit = async () => {
    // Step 1: Verify account details
    const isValid = verifyAccountDetails();
    if (!isValid) {
      return; // If validation fails, don't proceed to API call
    }

    console.log("Bank Details on submit:", bankDetails);

    // Step 2: Proceed with API call if validation is successful
    try {
      presentLoader(); // Show loader before API call starts

      const response = await getBankAccountVerificationStatusForEmandate(
        bankDetails,
        allotmentPaymentId,
        activeApplicant[0]?.nameAsPerPan || activeApplicant[0]?.name,
      );
      console.log("response from bank api --->", response);

      if (response && response.data.status === "success") {
        // Successful verification
        setIsVerified(true);
        setButtonText("Continue");

        // Extract bank name and branch from the response
        const bank =
          response?.data?.bankVerificationData?.data?.ifsc_details?.bank;
        const branch =
          response?.data?.bankVerificationData?.data?.ifsc_details?.branch;

        // Set bank name and branch name to state
        setBankName(bank);
        setBranchName(branch);

        // Update verification message
        setVerificationMessage(
          `Bank verification successful for ${bank} - ${branch}`,
        );
        setIsInitialVerificationDone(true); // Mark as verified

        // Show a success toast
        toast.success("Bank verification successful!");
      } else {
        // Failure in verification
        const failureMessage =
          response?.data?.msg || "Verification failed. Please try again.";
        setDisplayNameMismatchModal(true);
      }
    } catch (error) {
      console.error("Error fetching Bank Account Verification Status:", error);

      // Show a generic error message if something goes wrong
      const errorMessage =
        error?.response?.data?.msg ||
        "An error occurred. Please try again later.";

      console.log("failure message --->", errorMessage);

      setDisplayNameMismatchModal(true);
      setCustomerNameAsPerBankUPI(error?.response?.data?.nameAsPerBankUPI);
   
    } finally {
      // setIsLoading(false); // Stop loading once the operation is complete
      dismissLoader();
    }
  };

  const handleBackToAllotments = () => {
    navigate("/Home/EMandate");
  };

  const isFormValid = () => {
    return (
      bankDetails.accountNumber &&
      bankDetails.confirmAccountNumber &&
      bankDetails.ifscCode &&
      !errors.accountNumber &&
      !errors.confirmAccountNumber &&
      !errors.ifscCode
    );
  };

  return (
    <div style={{ overflow: "auto" }}>
      <div className={cssClasses.pageWrapper}>
        <EMandateStepper activeStep={activeStep} />
        <div className={cssClasses.wrapper}>
          {activeApplicant.length > 0 && (
            <Card className={cssClasses.mandateProgressCard} elevation={0}>
              <div className={cssClasses.mandateHeader}>
                <strong>E-Mandate Progress</strong>
                <span>
                  ({currentIndex}/{activeApplicant.length} completed)
                </span>
              </div>

              <div className={cssClasses.mandateFlow}>
                {activeApplicant.map((applicant, index) => (
                  <React.Fragment key={applicant.id}>
                    <div className={cssClasses.mandateUser}>
                      <div className={cssClasses.radioCircle} />
                      <div>
                        <div className={cssClasses.userName}>
                          {applicant?.name}
                        </div>
                        <div className={cssClasses.userMeta}>
                          {applicant.percentage || applicant?.splitRatio}% • ₹
                          {numberWithCommas(applicant?.splitAmount)}
                        </div>
                      </div>
                    </div>

                    {index !== activeApplicant.length - 1 && (
                      <div className={cssClasses.arrow}>→</div>
                    )}
                  </React.Fragment>
                ))}
              </div>
            </Card>
          )}
          {/* Header */}
          <div className={cssClasses.header}>
            <h2>Bank Verification</h2>
            <p>
              Enter bank details for{" "}
              {activeApplicant?.name || activeApplicant?.[0]?.name}
            </p>
          </div>

          {/* Form Card */}
          <Card className={cssClasses.formCard} elevation={0}>
            <div className={cssClasses.fieldGroup}>
              <label>Bank Account Number</label>
              <TextField
                placeholder="Enter account number"
                fullWidth
                value={bankDetails.accountNumber}
                onChange={(e) => handleChange("accountNumber", e.target.value)}
                error={!!errors.accountNumber}
                helperText={errors.accountNumber}
                FormHelperTextProps={{
                  style: {
                    fontSize: "12px", // Change the font size here
                  },
                }}
                InputProps={{
                  style: {
                    fontSize: "14px", // Change the font size of the input text here
                  },
                }}
              />
            </div>

            <div className={cssClasses.fieldGroup}>
              <label>Confirm Account Number</label>
              <TextField
                placeholder="Re-enter account number"
                fullWidth
                value={bankDetails.confirmAccountNumber}
                onChange={(e) =>
                  handleChange("confirmAccountNumber", e.target.value)
                }
                error={!!errors.confirmAccountNumber} // Display error only if error exists
                helperText={errors.confirmAccountNumber}
                FormHelperTextProps={{
                  style: {
                    fontSize: "12px", // Change the font size of helper text here
                  },
                }}
                InputProps={{
                  style: {
                    fontSize: "14px", // Change the font size of the input text here
                  },
                }}
              />
            </div>

            <div className={cssClasses.fieldGroup}>
              <label>IFSC Code</label>
              <TextField
                placeholder="E.G., HDFC0001234"
                fullWidth
                value={bankDetails.ifscCode}
                onChange={(e) => handleChange("ifscCode", e.target.value)}
                error={!!errors.ifscCode}
                helperText={errors.ifscCode}
                FormHelperTextProps={{
                  style: {
                    fontSize: "12px", // Change the font size here
                  },
                }}
                InputProps={{
                  style: {
                    fontSize: "14px", // Change the font size of the input text here
                  },
                }}
              />
            </div>
          </Card>

          {/* Bank verification status message */}
          {isVerified ? (
            <div
              style={{
                backgroundColor: "#e8f5e9", // Light green background for success
                padding: "12px 20px",
                borderRadius: "8px",
                margin: "15px 10px",
                display: "flex",
                alignItems: "center",
                border: "1px solid #29a37a", // Green border for the success message
              }}
            >
              <CheckCircleIcon
                style={{
                  color: "#16A34A", // Green color for the checkmark
                  fontSize: "24px",
                  marginRight: "15px",
                }}
              />
              <div>
                <p
                  style={{
                    margin: 0,
                    fontWeight: "500",
                    fontSize: "16px",
                    color: "#1d2530",
                    marginBottom: "4px",
                  }}
                >
                  Bank verification successful
                </p>
                {/* Display the bank and branch names */}
                <p
                  style={{
                    margin: 0,
                    fontSize: "14px",
                    color: "#616161",
                  }}
                >
                  {bankName} - {branchName}
                </p>
              </div>
            </div>
          ) : (
            <div
              style={{
                padding: "10px",
                borderRadius: "5px",
                marginBottom: "20px",
              }}
            >
              {/* Placeholder for the verification message */}
            </div>
          )}

          {/* Footer Buttons */}
          <div className={cssClasses.footerRow}>
            <Button
              btnName="Back"
              style={backBtnStyle}
              clicked={handleBackToAllotments}
            />

            {/* Only render the 'Verify Bank Account' button until verification is successful */}
            {!isVerified && (
              <Button
                btnName={buttonText}
                style={{
                  ...verifyBtnStyle,
                  backgroundColor:
                    isFormValid() && !isVerified ? "#163475ff" : "#94a3b8", // Dark color if valid, light color if invalid or disabled
                }}
                clicked={handleSubmit}
                disabled={isVerified || !isFormValid()}
              >
                {isLoading ? (
                  <CircularProgress size={24} color="inherit" /> // Show loader when loading
                ) : (
                  buttonText
                )}
              </Button>
            )}

            {/* Only show 'Continue' button after successful bank verification */}
            {isVerified && (
              <Button
                btnName="Continue"
                style={{
                  ...verifyBtnStyle,
                  backgroundColor: isFormValid() ? "#163475ff" : "#94a3b8", // Dark color for valid, light color for invalid
                }}
                clicked={() => {
                  console.log("➡ Proceeding to ConfirmPaymentSchedule");
                  console.log("➡ currentIndex:", currentIndex);
                  console.log("➡ activeApplicant:", activeApplicant);

                  if (isFormValid()) {
                    navigate("/Home/ConfirmPaymentSchedule", {
                      state: {
                        bankDetails,
                        bankName,
                        branchName,
                        // splitData,
                        currentIndex,
                        activeApplicant,
                        totalAmount,
                        totalMandateAmount,
                        bookingId,
                        allotment,
                        allotmentPaymentId,
                        // selectedApplicantData, // keep for single flow
                      },
                    });
                  } else {
                    setVerificationMessage(
                      "Please correct the errors before proceeding.",
                    );
                  }
                }}
                disabled={!isFormValid()} // Disable button if form is invalid
              />
            )}
          </div>
        </div>
      </div>

      {displayNameMismatchModal && (
        <CustomerBankInfoMismatchModal
          customerName={
            activeApplicant[0]?.nameAsPerPan || activeApplicant[0]?.name
          }
          bankVerifiedName={customerNameAsPerBankUPI || "N.A."}
          open={displayNameMismatchModal}
          handleClose={handleNameMismatchModalClose}
        />
      )}
    </div>
  );
};

export default BankVerification;

/* ---------- button styles ---------- */

const backBtnStyle = {
  height: "48px",
  borderRadius: "12px",
  border: "1px solid #d1d5db",
  backgroundColor: "#ffffff",
  color: "#0f172a",
  boxShadow: "none",
  fontSize: "1.52rem",
};

const verifyBtnStyle = {
  height: "48px",
  borderRadius: "12px",
  border: "none",
  backgroundColor: "#94a3b8", // Disabled color before success
  color: "#ffffff",
  fontSize: "1.52rem",
};
